import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useTheme, useMediaQuery, Box, Typography } from '@mui/material';
import { useContext, cloneElement } from 'react';
import { makeStyles } from 'tss-react/mui';
import { AuthContext, IconContext } from '@plumeuk/shapeshift-identity';
import 'x2js';

//Hook returns url path to server resources
//With strapi local dev path is relative
function useFileBaseUrl() {
    const { config } = useContext(AuthContext);
    const baseFileUrl = process.env.NODE_ENV === "production" ? "" : (config?.baseApiUrl ?? "");
    const getUrl = (e) => {
        if (e.startsWith("http"))
            return e;
        return baseFileUrl + e;
    };
    return { baseFileUrl, getUrl };
}

const useMq = () => {
    const theme = useTheme();
    const isMobileMq = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 1}px)`);
    const isTabletMq = useMediaQuery(`(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`);
    const isDesktopMq = useMediaQuery(`(min-width:${theme?.breakpoints.values.md + 1}px)`);
    return {
        mobile: isMobileMq,
        tablet: isTabletMq,
        desktop: isDesktopMq
    };
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

const useStyles = makeStyles()((theme) => ({
    file: {
        marginBottom: "20px",
        flexWrap: "wrap",
        display: "flex",
        borderRadius: "10px",
        backgroundColor: "#BCBCBD33",
        padding: "20px",
        "& a": {
            textDecoration: "none"
        },
        [theme.breakpoints.down("sm")]: {
            backgroundColor: "#fff",
            paddingBottom: "5px"
        }
    },
    fileIcon: {
        borderRadius: "15px",
        backgroundColor: theme.palette.secondary.main,
        color: "white",
        margin: "10px",
        padding: "15px",
        height: "fit-content",
        width: "fit-content",
        [theme.breakpoints.down("sm")]: {
            margin: "0"
        }
    },
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        flexGrow: 5,
        flexBasis: "10px",
        textAlign: "left",
        maxWidth: "calc(100% - 60px)",
        margin: "10px 30px",
        [theme.breakpoints.down("sm")]: {
            margin: "0 0 0 30px",
            wordBreak: "break-all"
        }
    },
    downloadButton: {
        cursor: "pointer",
        textAlign: "center",
        color: theme.palette.secondary.main,
        marginRight: "20px",
        margin: "10px",
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%",
            display: "flex",
            flexDirection: "row-reverse",
            justifyContent: "center",
            "& *": {
                display: "inline"
            },
            "& svg": {
                marginLeft: "5px"
            },
            marginTop: "20px",
            paddingTop: "10px",
            borderTop: "solid 1px " + theme.palette.secondary.main
        },
        [theme.breakpoints.up("sm")]: {
            borderLeft: "solid 1px #BCBCBD",
            paddingLeft: "30px"
        }
    },
    title: {},
    description: {
        color: "#2A2A2F",
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%",
            paddingTop: "20px"
        }
    },
    fileDownloadAnchor: {
        [theme.breakpoints.down("sm")]: {
            flexBasis: "100%"
        }
    }
}));
const File = ({ className, file, componentOverrides, actionOverrideDownload, ...props }) => {
    const { classes, cx } = useStyles();
    const { getUrl } = useFileBaseUrl();
    const mq = useMq();
    const { fileCopyIcon, downloadForOfflineIcon } = useContext(IconContext);
    const downloadBtn = (jsxs(Box, { className: classes.downloadButton, children: [componentOverrides?.downloadIcon ?? downloadForOfflineIcon, jsx(Typography, { children: "Download" })] }));
    const alternativeText = file.alternativeText || file.caption ? (jsx(Typography, { className: classes.description, children: file.caption ?? file.alternativeText })) : jsx(Fragment, {});
    return (jsxs(Box, { className: cx(classes.file, className), ...props, children: [jsx(Box, { className: classes.fileIcon, children: componentOverrides?.fileIcon ?? fileCopyIcon }), jsxs(Box, { className: classes.contentContainer, children: [jsx(Typography, { variant: "h6", className: classes.title, children: file.name }), !mq.mobile && alternativeText] }), mq.mobile && alternativeText, actionOverrideDownload
                ? cloneElement(downloadBtn, { onClick: actionOverrideDownload })
                : (jsx("a", { target: "_blank", rel: "noreferrer", href: getUrl(file.url), download: "", className: classes.fileDownloadAnchor, children: downloadBtn }))] }));
};

const Files = ({ files, ...props }) => (jsx(Box, { ...props, children: files.map((file, i) => (jsx(File, { file: file, "data-test-id": `file-${file.id}` }, `file-${i}`))) }));

export { File, Files };
